/**
 * Letter tracing — stroke-order aware, direction enforced.
 *
 * Isolated letter drills get skipped, so the spec carries a drawing to fuse the
 * writing into (`fuseWithDrawing`). The geometric checks below are what make
 * "top to bottom" real: a stroke declared top_to_bottom whose points run upward
 * is rejected rather than silently teaching the wrong habit.
 */

import { z } from 'zod'
import { IdSchema, NormalizedPointSchema, SentenceSchema, isUnique, signedArea, specFor } from '../common'
import { STROKE_DIRECTIONS } from '../strokes'

export const TRACING_GUIDES = ['full', 'dotted', 'start_dot', 'none'] as const

export const TracingStrokeSchema = z.object({
  id: IdSchema,
  /** Ordered points along the pencil's path. */
  points: z.array(NormalizedPointSchema).min(2).max(40),
  direction: z.enum(STROKE_DIRECTIONS),
  /** Said aloud as the stroke starts: "down the tall line". */
  startHint: SentenceSchema.optional(),
})

export const TracingLetterSchema = z.object({
  char: z.string().regex(/^[A-Za-z]$/, 'one letter at a time'),
  letterCase: z.enum(['upper', 'lower']),
  strokes: z.array(TracingStrokeSchema).min(1).max(4),
  /** A word that starts with it, for the sound: "A is for apple". */
  keyword: SentenceSchema.optional(),
})

export const LetterTracingConfigSchema = z
  .object({
    letters: z.array(TracingLetterSchema).min(1).max(8),
    /** Strokes must be traced in the order given. */
    enforceStrokeOrder: z.boolean(),
    /** A stroke drawn the wrong way round is redone, not accepted. */
    enforceDirection: z.boolean(),
    guide: z.enum(TRACING_GUIDES),
    /** How far off the path she can wander, normalized. */
    toleranceRadius: z.number().min(0.02).max(0.3).default(0.12),
    showLetterName: z.boolean().default(true),
    /** Writing lives inside drawing — a label to write on a picture she makes. */
    fuseWithDrawing: z.object({
      enabled: z.boolean(),
      prompt: SentenceSchema.optional(),
    }),
    /** Invented spelling is never corrected. */
    acceptInventedSpelling: z.literal(true).default(true),
  })
  .superRefine((config, ctx) => {
    if (!isUnique(config.letters.map((letter) => `${letter.char}${letter.letterCase}`))) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['letters'], message: 'the same letter is listed twice' })
    }
    if (config.fuseWithDrawing.enabled && !config.fuseWithDrawing.prompt) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fuseWithDrawing', 'prompt'],
        message: 'say what she should draw and label',
      })
    }

    for (const [letterIndex, letter] of config.letters.entries()) {
      const expectedCase = letter.char === letter.char.toUpperCase() ? 'upper' : 'lower'
      if (letter.letterCase !== expectedCase) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['letters', letterIndex, 'letterCase'],
          message: `"${letter.char}" is ${expectedCase}case`,
        })
      }
      if (!isUnique(letter.strokes.map((stroke) => stroke.id))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['letters', letterIndex, 'strokes'],
          message: 'stroke ids must be unique',
        })
      }
      for (const [strokeIndex, stroke] of letter.strokes.entries()) {
        const path = ['letters', letterIndex, 'strokes', strokeIndex, 'points']
        const first = stroke.points[0]
        const last = stroke.points[stroke.points.length - 1]
        const fail = (message: string): void => {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path, message })
        }
        switch (stroke.direction) {
          case 'top_to_bottom':
            if (last.y <= first.y) fail('a top-to-bottom stroke must end lower than it starts')
            break
          case 'bottom_to_top':
            if (last.y >= first.y) fail('a bottom-to-top stroke must end higher than it starts')
            break
          case 'left_to_right':
            if (last.x <= first.x) fail('a left-to-right stroke must end further right than it starts')
            break
          case 'right_to_left':
            if (last.x >= first.x) fail('a right-to-left stroke must end further left than it starts')
            break
          case 'clockwise':
            if (stroke.points.length < 3) fail('a curved stroke needs at least three points')
            else if (signedArea(stroke.points) <= 0) fail('these points travel counterclockwise')
            break
          case 'counterclockwise':
            if (stroke.points.length < 3) fail('a curved stroke needs at least three points')
            else if (signedArea(stroke.points) >= 0) fail('these points travel clockwise')
            break
          case 'freeform':
            break
        }
      }
    }
  })

export type LetterTracingConfig = z.infer<typeof LetterTracingConfigSchema>

export const LetterTracingSpecSchema = specFor('letter_tracing', LetterTracingConfigSchema)
